// Base
import Image from 'next/image'
import { useState } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'

// Packages
import { Swiper, SwiperSlide } from 'swiper/react'

// Components
import { Button, Container, Flex, Text } from 'yard-utilities/components'
import Span from '@components/v21/common/span/Span'
import Section from '@components/v21/common/section/Section'
import { Input } from '@components/v21/common/input/Input'
import BackButton from '@components/v21/back-button/BackButton'
import LocationPopup from './elements/location-popup/LocationPopup'
import LocationTag from './elements/location-tag/LocationTag'

// Assets
import HeroImage from '@images/v21/photos/location-bg.png'
import { LocationIcon } from 'icons/v21/Icons'

// Styles
import styles from './LocationWrapper.module.sass'

export const locationTags = [
  {
    id: 82,
    name: 'Los Angeles, CA'
  },
  { id: 80, name: 'Atlanta, GA' },
  { id: 81, name: 'Chicago, IL' },
  { id: 84, name: 'New York City, NY' }
]

export interface CityData {
  id: number
  name: string
}

interface LocationWrapperProps {
  pathname?: string
  backButtonURL?: string
}

const LocationWrapper = ({ pathname, backButtonURL }: LocationWrapperProps) => {
  const [location, setLocation] = useState('')
  const [selection, setSelection] = useState<CityData>({
    id: 0,
    name: ''
  })
  const [displayPopup, setDisplayPopup] = useState(false)

  const router = useRouter()

  const setActiveTag = (id: number, name: string) => {
    setLocation(name)
    setSelection({ id, name })
  }

  return (
    <Section padding={[16, 0, 24]} spaceBetween className={styles.section}>
      {displayPopup && (
        <LocationPopup
          location={location}
          setLocation={setLocation}
          setSelection={setSelection}
          setDisplayPopup={setDisplayPopup}
        />
      )}
      <Image
        src={HeroImage}
        fill
        sizes="(max-width: 768px) 600px, (max-width: 1200px) 800px, 1000px"
        priority
        placeholder="blur"
        alt="Location hero image"
        className={styles.image}
      />
      <Container>
        <BackButton
          href={{
            pathname: backButtonURL || '/v21',
            query: { ...router.query }
          }}
        />
        <Flex
          flexDirection="column"
          justifyContent="space-between"
          className={styles.container}
        >
          <div>
            <Text
              tagName="h2"
              variant="title-2"
              spacing={{ sm: 16 }}
              align="center"
            >
              Choose Your Departure Location
            </Text>
            <Text variant="body-8" align="center" spacing={{ sm: 96 }}>
              We deliver travel deals from{' '}
              <span className="yellow-highlight">
                <Span fontWeight={600}>your home</Span>
              </span>{' '}
              (or surrounding){' '}
              <span className="yellow-highlight">
                <Span fontWeight={600}>Airport.</Span>
              </span>
            </Text>

            <Text variant="body-6" fontWeight={600} spacing={{ sm: 8 }}>
              Trending departures! 🔥
            </Text>
            <Swiper
              slidesPerView={'auto'}
              spaceBetween={8}
              className={styles.swiperContainer}
            >
              {locationTags.map((tag, i) => (
                <SwiperSlide
                  key={i}
                  className={styles.slide}
                  onClick={() => setActiveTag(tag.id, tag.name)}
                  role="button"
                >
                  <LocationTag key={i} location={location}>
                    {tag.name}
                  </LocationTag>
                </SwiperSlide>
              ))}
            </Swiper>

            <Text variant="body-6" fontWeight={600} spacing={{ sm: 8 }}>
              Departure location
            </Text>

            <Flex
              flexDirection="column"
              rowGap={{ sm: 16 }}
              spacing={{ sm: 16 }}
            >
              <Input
                startIcon={<LocationIcon />}
                placeholder="Type your location"
                id="location"
                name="location"
                value={location}
                readOnly
                onClick={() => {
                  setDisplayPopup(true)
                }}
              />

              <Button
                variant="yellow"
                as={Link}
                disabled={selection.name == ''}
                href={{
                  pathname: pathname || '/v21/type-of-travel',
                  query: {
                    city: selection.id,
                    location: selection.name
                  }
                }}
              >
                GET FIRST HACKED TRAVEL DEALS
              </Button>
            </Flex>
          </div>
          <Text variant="body-2" align="center">
            🔥 <Span fontWeight={600}>Only 39 slots</Span> available today!
            Hurry up!
          </Text>
        </Flex>
      </Container>
    </Section>
  )
}

export default LocationWrapper
